import React, { useState, useRef, useEffect } from "react";
import "./AudioRecordModal.css";
import MicRecorder from "mic-recorder-to-mp3";
import { translations } from "../../utils/translations";

const AudioRecordModal = ({ isOpen, onClose, onSend }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [, setIsPlaying] = useState(false);
  const recorder = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const currentLang = localStorage.getItem("language") || "es";
  const t =
    translations[currentLang]?.chatComponent ||
    translations["es"].chatComponent;

  useEffect(() => {
    recorder.current = new MicRecorder({ bitRate: 128 });
  }, []);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  if (!isOpen) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startRecording = () => {
    if (recorder.current) {
      recorder.current
        .start()
        .then(() => {
          setIsRecording(true);
          setRecordingTime(0);
        })
        .catch((error) => {
          console.error("Error al acceder al micrófono:", error);
          alert(t.errorMicrophoneAccess);
        });
    }
  };

  const stopRecording = () => {
    if (recorder.current) {
      recorder.current
        .stop()
        .getMp3()
        .then(([buffer, blob]) => {
          const url = URL.createObjectURL(blob);
          setAudioBlob(blob);
          setAudioUrl(url);
          setIsRecording(false);
        })
        .catch((e) => {
          console.error("Error al detener la grabación:", e);
          alert(t.errorRecordingStop);
          setIsRecording(false);
        });
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleRecordAgain = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const handleSend = () => {
    if (audioBlob) {
      const audioFile = new File([audioBlob], "audio-recording.mp3", {
        type: "audio/mpeg",
      });
      onSend(audioFile);
      handleClose();
    }
  };

  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsRecording(false);
    setIsPlaying(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="audio-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="audio-modal-header">
          <h2>{t.modalTitle}</h2>
          <button className="close-button" onClick={handleClose}>
            <p>X</p>
          </button>
        </div>

        <div className="audio-recording-area">
          {!isRecording && !audioBlob && (
            <div className="audio-idle-state">
              <div className="mic-icon-container idle">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"
                    fill="#999"
                  />
                  <path
                    d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"
                    stroke="#999"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="audio-status">{t.statusIdle}</p>
            </div>
          )}

          {isRecording && (
            <div className="audio-recording-state">
              <div className="mic-icon-container recording">
                <div className="pulse-ring"></div>
                <div className="pulse-ring delay"></div>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"
                    fill="white"
                  />
                  <path
                    d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="recording-time">{formatTime(recordingTime)}</p>
              <p className="audio-status">{t.statusRecording}</p>{" "}
            </div>
          )}

          {audioBlob && !isRecording && (
            <div className="audio-recorded-state">
              <div className="mic-icon-container recorded">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"
                    fill="white"
                  />
                  <path
                    d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="audio-status">{t.statusRecorded}</p>
              <button className="play-audio-button" onClick={playAudio}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M8 5v14l11-7z" fill="#5b8def" />
                </svg>
              </button>
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={handleAudioEnded}
                style={{ display: "none" }}
              />
            </div>
          )}
        </div>

        <div className="audio-modal-actions">
          {!isRecording && !audioBlob && (
            <button className="record-button" onClick={startRecording}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8" fill="white" />
              </svg>
              {t.buttonRecord}
            </button>
          )}

          {isRecording && (
            <button className="stop-recording-button" onClick={stopRecording}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="6" y="6" width="12" height="12" fill="white" />
              </svg>
              {t.buttonStop}
            </button>
          )}

          {audioBlob && !isRecording && (
            <>
              <button
                className="record-again-button"
                onClick={handleRecordAgain}
              >
                {t.buttonRecordAgain}
              </button>
              <div className="audio-send-actions">
                <button className="cancel-button" onClick={handleClose}>
                  {t.buttonCancel}
                </button>
                <button className="send-audio-button" onClick={handleSend}>
                  {t.buttonSend}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioRecordModal;
